import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import api, { RBAC_BASE } from '../../../../../api/rbacApi';
import { employeeApi } from '../../../../../api/employeeApi';
import { useNavigate } from 'react-router-dom';

const AddEmployees = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    employeeName: '',
    employeeCompany: '',
    employeeProfile: null,
    gender: '',
    joiningDate: '',
    dob: '',
    password: '',
    email: '',
    phone: '',
    department: '',
    designation: '',
    designationId: '',
    address: ''
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRoleLoading(true);
        const res = await api.get(`${RBAC_BASE}/roles`);
        const options = Array.isArray(res.data)
          ? res.data.filter((role) => role.bitStatus !== 0)
          : [];
        setRoleOptions(options);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load roles.');
      } finally {
        setRoleLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const showToast = (icon, title) => {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon,
      title,
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
    });
  };

  const validateForm = () => {
    if (!formData.employeeName.trim()) {
      return 'Employee Name is required.';
    }

    if (!String(formData.phone || '').trim()) {
      return 'Mobile Number is required.';
    }

    if (!String(formData.password || '').trim()) {
      return 'Password is required.';
    }

    if (!String(formData.designationId || '').trim()) {
      return 'Role is required.';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      showToast('error', validationError);
      return;
    }

    setLoading(true);

    try {
      await employeeApi.addEmployee(formData);
      setFormData({
        employeeName: '',
        employeeCompany: '',
        employeeProfile: null,
        gender: '',
        joiningDate: '',
        dob: '',
        password: '',
        email: '',
        phone: '',
        department: '',
        designation: '',
        designationId: '',
        address: ''
      });
      showToast('success', 'Employee added successfully');
      navigate('/employees-all');
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.response?.data?.message ||
          err?.message ||
          'Failed to add employee. Please try again.';
      setError(message);
      showToast('error', message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  const handleDesignationChange = (e) => {
    const selectedId = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      designationId: selectedId,
    }));
  };

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div>
          <h3 className="fw-bold mb-1">Register Employees</h3>
          <small className="text-muted">Add a new employee and configure their organizational details and HRMS account.</small>
        </div>
      </div>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4 p-xl-5">
          <form className="row g-3 g-xl-4" onSubmit={handleSubmit}>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  name="employeeName"
                  value={formData.employeeName}
                  onChange={handleInputChange}
                  className="form-control"
                  id="employeeName"
                  placeholder="Employee Name"
                />
                <label htmlFor="employeeName">Employee Name</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="form-select"
                  id="gender"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <label htmlFor="gender">Gender</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  className="form-control"
                  id="dob"
                  placeholder="Date of Birth"
                />
                <label htmlFor="dob">Date of Birth</label>
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label text-muted">Employee Profile</label>
              <input
                className="form-control"
                type="file"
                name="employeeProfile"
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  name="employeeCompany"
                  value={formData.employeeCompany}
                  onChange={handleInputChange}
                  className="form-control"
                  id="employeeCompany"
                  placeholder="Company Name"
                />
                <label htmlFor="employeeCompany">Employee Company</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  className="form-control"
                  id="department"
                  placeholder="Department"
                />
                <label htmlFor="department">Department / Unit</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  className="form-control"
                  id="designation"
                  placeholder="Designation"
                />
                <label htmlFor="designation">Designation</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <select
                  name="designationId"
                  value={formData.designationId}
                  onChange={handleDesignationChange}
                  className="form-select"
                  id="role"
                  disabled={roleLoading}
                >
                  <option value="">
                    {roleLoading ? "Loading roles..." : "Select Role"}
                  </option>
                  {roleOptions.map((role) => (
                    <option key={role.intDesigId} value={role.intDesigId}>
                      {role.nvchDesigName}
                    </option>
                  ))}
                </select>
                <label htmlFor="role">Role</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="date"
                  name="joiningDate"
                  value={formData.joiningDate}
                  onChange={handleInputChange}
                  className="form-control"
                  id="joiningDate"
                  placeholder="Joining Date"
                />
                <label htmlFor="joiningDate">Joining Date</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-control"
                  id="phone"
                  placeholder="Phone Number"
                />
                <label htmlFor="phone">Phone Number</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-control"
                  id="email"
                  placeholder="Email ID"
                />
                <label htmlFor="email">Email ID</label>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="form-control"
                  id="password"
                  placeholder="Password"
                />
                <label htmlFor="password">Password</label>
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="form-control"
                  id="address"
                  rows="3"
                  placeholder="Employee Address"
                ></textarea>
                <label htmlFor="address">Address</label>
              </div>
            </div>

            {error && (
              <div className="col-12">
                <div
                  className="alert alert-danger alert-dismissible fade show rounded-3"
                  role="alert"
                >
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="alert"
                    aria-label="Close"
                  ></button>
                </div>
              </div>
            )}

            <div className="col-12 d-flex justify-content-end pt-2">
              <button
                type="submit"
                className="btn btn-primary btn-lg px-4"
                disabled={loading || roleLoading}
              >
                {loading ? "Adding Employee..." : "Register Employee"}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEmployees;